/**
 * Product Form LOP - JavaScript para funcionalidad de swatches en formulario de producto
 * Versión: 1.0.0
 * Autor: LOP Development Team
 */

(function() {
  'use strict';

  // Configuración
  const LOP_CONFIG = {
    navigationDelay: 300,
    loadingClass: 'loading',
    errorClass: 'error'
  };

  // Clases CSS
  const LOP_CLASSES = {
    swatchItem: 'LOP-product-form-swatch-item',
    swatchLink: 'LOP-product-form-swatch-link',
    swatchImage: 'LOP-product-form-swatch-image'
  };

  /**
   * Clase principal para manejar Product Form LOP
   */
  class ProductFormLOP {
    constructor() {
      this.initialized = false;
      this.init();
    }

    /**
     * Inicializar la funcionalidad
     */
    init() {
      if (this.initialized) return;
      
      this.bindEvents();
      this.initialized = true;
      
      // Log de inicialización
      console.log('Product Form LOP inicializado');
    }

    /**
     * Vincular eventos
     */
    bindEvents() {
      // Helper: e.target puede ser un nodo de texto (no tiene .closest)
      const getTargetElement = (e) => {
        const t = e.target;
        return t && t.nodeType === 1 ? t : (t ? t.parentElement : null);
      };

      // Eventos de click en swatches
      document.addEventListener('click', (e) => {
        const el = getTargetElement(e);
        if (el && el.closest(`.${LOP_CLASSES.swatchItem}`)) {
          this.handleSwatchClick(e);
        }
      });

      // Eventos de hover para tooltips
      document.addEventListener('mouseenter', (e) => {
        const el = getTargetElement(e);
        if (el && el.closest(`.${LOP_CLASSES.swatchItem}`)) {
          this.handleSwatchHover(e);
        }
      }, true);

      // Eventos de focus para accesibilidad
      document.addEventListener('focusin', (e) => {
        const el = getTargetElement(e);
        if (el && el.closest(`.${LOP_CLASSES.swatchItem}`)) {
          this.handleSwatchFocus(e);
        }
      });

      // Soporte para contenido dinámico/AJAX
      this.observeDOMChanges();
    }

    /**
     * Manejar click en swatch
     */
    handleSwatchClick(e) {
      const targetEl = e.target && e.target.nodeType === 1 ? e.target : (e.target && e.target.parentElement);
      const swatchItem = targetEl && targetEl.closest(`.${LOP_CLASSES.swatchItem}`);
      if (!swatchItem) return;

      const productHandle = swatchItem.dataset.productHandle;
      if (!productHandle) {
        console.error('Product Form LOP: No se encontró product handle');
        return;
      }

      // Verificar si es el producto actual
      const isCurrentProduct = swatchItem.classList.contains('LOP-product-form-swatch-item--current');
      if (isCurrentProduct) {
        // Si es el producto actual, solo mostrar un mensaje o hacer scroll suave
        console.log('Product Form LOP: Ya estás viendo este producto');
        return;
      }

      // Prevenir el comportamiento por defecto
      e.preventDefault();
      e.stopPropagation();

      // Añadir clase de carga
      swatchItem.classList.add(LOP_CONFIG.loadingClass);

      // Navegar al producto después del delay
      setTimeout(() => {
        try {
          window.location.href = `/products/${productHandle}`;
        } catch (error) {
          console.error('Product Form LOP: Error al navegar:', error);
          swatchItem.classList.remove(LOP_CONFIG.loadingClass);
          swatchItem.classList.add(LOP_CONFIG.errorClass);
        }
      }, LOP_CONFIG.navigationDelay);
    }

    /**
     * Manejar hover en swatch
     */
    handleSwatchHover(e) {
      const targetEl = e.target && e.target.nodeType === 1 ? e.target : (e.target && e.target.parentElement);
      const swatchItem = targetEl && targetEl.closest(`.${LOP_CLASSES.swatchItem}`);
      if (!swatchItem) return;

      // Tooltip ya está manejado por CSS
      // Aquí se pueden añadir funcionalidades adicionales
    }

    /**
     * Manejar focus en swatch
     */
    handleSwatchFocus(e) {
      const targetEl = e.target && e.target.nodeType === 1 ? e.target : (e.target && e.target.parentElement);
      const swatchItem = targetEl && targetEl.closest(`.${LOP_CLASSES.swatchItem}`);
      if (!swatchItem) return;

      // Asegurar que el swatch sea visible
      swatchItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    /**
     * Observar cambios en el DOM para contenido dinámico
     */
    observeDOMChanges() {
      if (!window.MutationObserver) return;

      const observer = new MutationObserver((mutations) => {
        let shouldReinit = false;
        
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                if (node.classList && node.classList.contains(LOP_CLASSES.swatchItem)) {
                  shouldReinit = true;
                }
                if (node.querySelector && node.querySelector(`.${LOP_CLASSES.swatchItem}`)) {
                  shouldReinit = true;
                }
              }
            });
          }
        });

        if (shouldReinit) {
          // Los swatches ya están funcionando, no necesitamos reinicializar
          // pero podemos loguear para debugging
          console.log('Product Form LOP: Nuevos swatches detectados');
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    /**
     * Crear swatch programáticamente
     */
    createSwatch(productData) {
      const { handle, title, image, tooltip } = productData;
      
      const swatchItem = document.createElement('div');
      swatchItem.className = LOP_CLASSES.swatchItem;
      swatchItem.dataset.productHandle = handle;
      
      if (tooltip) {
        swatchItem.dataset.tooltip = tooltip;
      }

      const swatchLink = document.createElement('a');
      swatchLink.className = LOP_CLASSES.swatchLink;
      swatchLink.href = `/products/${handle}`;
      swatchLink.setAttribute('aria-label', `Ver ${title}`);

      const swatchImage = document.createElement('img');
      swatchImage.className = LOP_CLASSES.swatchImage;
      swatchImage.src = image;
      swatchImage.alt = title;
      swatchImage.loading = 'lazy';

      swatchLink.appendChild(swatchImage);
      swatchItem.appendChild(swatchLink);

      return swatchItem;
    }

    /**
     * Añadir swatches a un contenedor
     */
    addSwatchesToContainer(container, products) {
      if (!container || !products || !Array.isArray(products)) return;

      products.slice(0, 5).forEach((product) => {
        const swatch = this.createSwatch(product);
        container.appendChild(swatch);
      });
    }

    /**
     * Obtener swatches existentes
     */
    getSwatches() {
      return document.querySelectorAll(`.${LOP_CLASSES.swatchItem}`);
    }

    /**
     * Verificar si un elemento es un swatch
     */
    isSwatch(element) {
      return element && element.classList && element.classList.contains(LOP_CLASSES.swatchItem);
    }

    /**
     * Obtener información del producto desde un swatch
     */
    getProductInfoFromSwatch(swatchItem) {
      if (!this.isSwatch(swatchItem)) return null;

      return {
        handle: swatchItem.dataset.productHandle,
        title: swatchItem.querySelector('img')?.alt || '',
        image: swatchItem.querySelector('img')?.src || '',
        tooltip: swatchItem.dataset.tooltip || ''
      };
    }

    /**
     * Verificar formato de metafields desde JavaScript
     */
    debugMetafieldFormat() {
      console.log('Product Form LOP: Verificando formato de metafields...');
      
      // Buscar elementos de debug en el DOM
      const debugElements = document.querySelectorAll('[style*="display: none"]');
      let debugInfo = null;
      
      debugElements.forEach(element => {
        if (element.textContent.includes('Debug Product Form - Agrupador Original:')) {
          debugInfo = element.textContent;
        }
      });
      
      if (debugInfo) {
        console.log('Product Form LOP: Información de debug encontrada:', debugInfo);
        
        // Extraer información específica
        const originalMatch = debugInfo.match(/Debug Product Form - Agrupador Original: ([^\n]+)/);
        const handlesMatch = debugInfo.match(/Debug Product Form - Handles Encontrados: ([^\n]+)/);
        
        if (originalMatch) {
          console.log('Product Form LOP: Formato original:', originalMatch[1].trim());
        }
        if (handlesMatch) {
          console.log('Product Form LOP: Handles encontrados:', handlesMatch[1].trim());
        }
      } else {
        console.log('Product Form LOP: No se encontró información de debug en el DOM');
      }
      
      // Verificar swatches existentes
      const swatches = document.querySelectorAll(`.${LOP_CLASSES.swatchItem}`);
      console.log(`Product Form LOP: Swatches encontrados: ${swatches.length}`);
      
      swatches.forEach((swatch, index) => {
        const handle = swatch.dataset.productHandle;
        const color = swatch.dataset.productColor;
        const isCurrent = swatch.dataset.isCurrentProduct;
        
        console.log(`Product Form LOP: Swatch ${index + 1}:`, {
          handle: handle,
          color: color,
          isCurrent: isCurrent,
          hasImage: !!swatch.querySelector('img')
        });
      });
      
      return {
        debugInfo: debugInfo,
        swatchesCount: swatches.length
      };
    }
  }

  // Inicializar cuando el DOM esté listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.ProductFormLOP = new ProductFormLOP();
    });
  } else {
    window.ProductFormLOP = new ProductFormLOP();
  }

  // Exponer la clase para uso externo
  window.ProductFormLOPClass = ProductFormLOP;

  // Función de utilidad para inicialización manual
  window.initProductFormLOP = function() {
    if (!window.ProductFormLOP) {
      window.ProductFormLOP = new ProductFormLOP();
    }
    return window.ProductFormLOP;
  };

  // Exponer funciones para debugging
  window.debugProductFormLOP = function() {
    if (window.ProductFormLOP) {
      return window.ProductFormLOP.debugMetafieldFormat();
    } else {
      console.log('Product Form LOP: Instancia no encontrada');
      return null;
    }
  };

  // Función para verificar formato de metafields
  window.debugProductFormMetafields = function() {
    if (window.ProductFormLOP) {
      return window.ProductFormLOP.debugMetafieldFormat();
    } else {
      console.log('Product Form LOP: Instancia no encontrada');
      return null;
    }
  };

})(); 